import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { filter, first, map, Observable, switchMap, take, tap } from 'rxjs';
import { getCurrentUser, getCurrentUserFailed } from 'src/app/data/store/actions/users.actions';
import { IUsersState } from 'src/app/data/store/models/users.model';
import { selectUsersState } from 'src/app/data/store/selectors/users.selectors';
import { befreeUrlValues } from '../constants';
import { IToken } from '../interfaces';
import { StorageService } from '../services/storage/storage.service';
import { StorageType } from '../services/storage/storage.type';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  private _router = inject(Router);
  private _store = inject(Store);
  private _storageService = inject(StorageService);

  private _auth: UrlTree;

  private _update: UrlTree;

  private _dashboard: UrlTree;

  public selectUsersState$: Observable<IUsersState> = this._store.select(selectUsersState);

  constructor() {
    this._auth = this._router.parseUrl(befreeUrlValues.auth);
    this._update = this._router.parseUrl(befreeUrlValues.update);
    this._dashboard = this._router.parseUrl(befreeUrlValues.dashboard);
  }

  public canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    return this._storageService.getItem<IToken>(StorageType.Token).pipe(
      take(1),
      switchMap((token) => {
        return this.selectUsersState$.pipe(
          tap((state) => {
            if (!state?.currentUser && !state?.isLoading && token?.access_token && !state?.error) {
              if (typeof navigator !== 'undefined' && !navigator.onLine) {
                this._store.dispatch(
                  getCurrentUserFailed({
                    error: new HttpErrorResponse({
                      status: 0,
                      statusText: 'Offline',
                      url: '/api/v1/users/current',
                    }),
                  }),
                );
              } else {
                this._store.dispatch(getCurrentUser());
              }
            }
          }),
          filter((state) => {
            return (
              (!state.isLoading && (Boolean(state.error) || Boolean(state.currentUser))) ||
              !token?.access_token
            );
          }),
          map((state) => this._defineActiveRoute(route, state)),
          first(),
        );
      }),
    );
  }

  private _defineActiveRoute(
    route: ActivatedRouteSnapshot,
    userState: IUsersState,
  ): boolean | UrlTree {
    if (!userState.currentUser) {
      if (route.routeConfig?.path === befreeUrlValues.auth) {
        return true;
      }
      return this._auth;
    } else {
      if (
        route.routeConfig?.path === befreeUrlValues.auth &&
        this._hasChildRoute(route, befreeUrlValues.passwordReset)
      ) {
        return true;
      }

      if (
        route.routeConfig?.path === befreeUrlValues.auth ||
        route.routeConfig?.path === befreeUrlValues.update
      ) {
        return this._dashboard;
      }
      return true;
    }
  }

  private _hasChildRoute(route: ActivatedRouteSnapshot, path: string): boolean {
    return route.children.some(
      (childRoute) =>
        childRoute.routeConfig?.path === path || this._hasChildRoute(childRoute, path),
    );
  }
}
